import { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Container,
    Grid,
    Snackbar,
    Alert,
    Stack,
    Divider,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    Send as SendIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';
import { getClientPrincipal } from '../services/auth';
import {
    submitPPD,
    fetchCompetencies,
    fetchLineManagerEmail,
    Competency,
    PPDFormClient
} from '../services/ppd';
import {
    sendPPDSubmissionNotification,
    sendPPDSubmissionConfirmation
} from '../services/email';
import { useNavigate } from 'react-router-dom';

interface PPDFormData {
    // Header fields
    job_title: string;
    activity: string;
    provider_if_external: string;
    duration: string;
    cost_gbp: string;
    manager_email: string;
    
    // 9 Questions
    development_need: string;
    selected_competencies: number[];
    options_considered: string;
    resources_required: string;
    timescales: string;
    effectiveness_assessment: string;
    expected_changes: string;
    knowledge_sharing: string;
    proposed_option: string;
}

const defaultFormData: PPDFormData = {
    job_title: '',
    activity: '',
    provider_if_external: '',
    duration: '',
    cost_gbp: '',
    manager_email: '',
    development_need: '',
    selected_competencies: [],
    options_considered: '',
    resources_required: '',
    timescales: '',
    effectiveness_assessment: '',
    expected_changes: '',
    knowledge_sharing: '',
    proposed_option: ''
};

const PPDFormPage = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<PPDFormData>(defaultFormData);
    const [competencies, setCompetencies] = useState<Competency[]>([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [isLoadingLineManager, setIsLoadingLineManager] = useState(false);

    // Load competencies and line manager on mount
    useEffect(() => {
        loadCompetencies();
        loadLineManager();
    }, []);

    const loadCompetencies = async () => {
        try {
            const data = await fetchCompetencies();
            setCompetencies(data);
        } catch (error) {
            console.error('Error loading competencies:', error);
            setSnackbarMessage('Failed to load competency options');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const loadLineManager = async () => {
        try {
            setIsLoadingLineManager(true);
            const principal = await getClientPrincipal();
            
            if (!principal?.userDetails) {
                console.log('User not authenticated, cannot load line manager');
                return;
            }

            const lineManagerEmail = await fetchLineManagerEmail(principal.userDetails);
            
            if (lineManagerEmail) {
                setFormData(prev => ({ ...prev, manager_email: lineManagerEmail }));
                console.log('Line manager auto-populated:', lineManagerEmail);
            } else {
                console.log('No line manager found for user, manual entry required');
            }
        } catch (error) {
            console.error('Error loading line manager:', error);
        } finally {
            setIsLoadingLineManager(false);
        }
    };

    const validateForm = (): boolean => {
        // Check required fields
        if (!formData.manager_email) {
            setSnackbarMessage('Manager\'s email address is required');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return false;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.manager_email)) {
            setSnackbarMessage('Please enter a valid email address for your manager');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return false;
        }

        // Check at least some content
        if (!formData.activity && !formData.development_need) {
            setSnackbarMessage('Please provide at least the activity or development need');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;

            setIsSubmitting(true);
            const principal = await getClientPrincipal();
            
            if (!principal?.userDetails || !principal?.name) {
                setSnackbarMessage('User not authenticated');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                setIsSubmitting(false);
                return;
            }

            // Create PPD form object
            const ppdForm: PPDFormClient = {
                owner_email: principal.userDetails,
                owner_name: principal.name,
                manager_email: formData.manager_email,
                status: 'pending',
                job_title: formData.job_title,
                activity: formData.activity,
                provider_if_external: formData.provider_if_external,
                duration: formData.duration,
                cost_gbp: parseFloat(formData.cost_gbp) || 0,
                development_need: formData.development_need,
                selected_competencies: formData.selected_competencies,
                options_considered: formData.options_considered,
                resources_required: formData.resources_required,
                timescales: formData.timescales,
                effectiveness_assessment: formData.effectiveness_assessment,
                expected_changes: formData.expected_changes,
                knowledge_sharing: formData.knowledge_sharing,
                proposed_option: formData.proposed_option
            };

            // Submit the PPD form
            const submittedForm = await submitPPD(ppdForm);

            // Send email notifications
            await sendPPDSubmissionNotification({
                owner_email: principal.userDetails,
                owner_name: principal.name,
                manager_email: formData.manager_email,
                job_title: formData.job_title,
                activity: formData.activity,
                development_need: formData.development_need,
                id: submittedForm.id!
            });

            await sendPPDSubmissionConfirmation({
                owner_email: principal.userDetails,
                owner_name: principal.name,
                manager_email: formData.manager_email,
                activity: formData.activity
            });

            // Show success message
            setSnackbarMessage('PPD form submitted successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setConfirmDialogOpen(false);

            // Reset form and navigate after delay
            setTimeout(() => {
                setFormData(defaultFormData);
                navigate('/my-ppd');
            }, 2000);

        } catch (error) {
            console.error('Error submitting PPD:', error);
            setSnackbarMessage('Failed to submit PPD form. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCompetencyChange = (competencyId: number) => {
        setFormData(prev => ({
            ...prev,
            selected_competencies: prev.selected_competencies.includes(competencyId)
                ? prev.selected_competencies.filter(id => id !== competencyId)
                : [...prev.selected_competencies, competencyId]
        }));
    };

    const handleCancel = () => {
        navigate('/my-ppd');
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#009688', mb: 3 }}>
                    Personal and Professional Development (PPD) Form
                </Typography>

                <Stack spacing={3}>
                    {/* Header Section */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Job Title"
                                value={formData.job_title}
                                onChange={(e) => setFormData(prev => ({ ...prev, job_title: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                required
                                label="Line Manager"
                                type="email"
                                value={formData.manager_email}
                                onChange={(e) => setFormData(prev => ({ ...prev, manager_email: e.target.value }))}
                                disabled={isLoadingLineManager}
                                helperText={isLoadingLineManager 
                                    ? "Loading line manager..." 
                                    : formData.manager_email 
                                        ? "Auto-populated from Office 365 (editable if needed)"
                                        : "Required - Your line manager's email for notifications"}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Activity"
                                placeholder="e.g., Training course, Conference, Workshop"
                                value={formData.activity}
                                onChange={(e) => setFormData(prev => ({ ...prev, activity: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Provider (if external)"
                                value={formData.provider_if_external}
                                onChange={(e) => setFormData(prev => ({ ...prev, provider_if_external: e.target.value }))}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Duration"
                                placeholder="e.g., 2 days, 1 week"
                                value={formData.duration}
                                onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Cost (GBP)"
                                type="number"
                                value={formData.cost_gbp}
                                onChange={(e) => setFormData(prev => ({ ...prev, cost_gbp: e.target.value }))}
                                InputProps={{
                                    startAdornment: <Typography sx={{ mr: 1 }}>£</Typography>,
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Development Questions
                    </Typography>

                    {/* Question 1: Development Need */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            1. What is the personal or professional development need?
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.development_need}
                            onChange={(e) => setFormData(prev => ({ ...prev, development_need: e.target.value }))}
                            placeholder="Describe the development need this activity will address..."
                        />
                    </Box>

                    {/* Question 2: Competencies (Multi-select) */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            2. Which competency(ies) will this development address? (Select all that apply)
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                            <FormGroup>
                                {competencies.map((competency) => (
                                    <FormControlLabel
                                        key={competency.id}
                                        control={
                                            <Checkbox
                                                checked={formData.selected_competencies.includes(competency.id)}
                                                onChange={() => handleCompetencyChange(competency.id)}
                                            />
                                        }
                                        label={competency.competency_name}
                                    />
                                ))}
                            </FormGroup>
                        </Paper>
                    </Box>

                    {/* Question 3: Options Considered */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            3. What other options have been considered?
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.options_considered}
                            onChange={(e) => setFormData(prev => ({ ...prev, options_considered: e.target.value }))}
                            placeholder="List alternative development options that were considered..."
                        />
                    </Box>

                    {/* Question 4: Resources Required */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            4. What resources are required?
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.resources_required}
                            onChange={(e) => setFormData(prev => ({ ...prev, resources_required: e.target.value }))}
                            placeholder="Detail the resources needed (time, budget, materials, etc.)..."
                        />
                    </Box>

                    {/* Question 5: Timescales */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            5. What are the timescales?
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={formData.timescales}
                            onChange={(e) => setFormData(prev => ({ ...prev, timescales: e.target.value }))}
                            placeholder="Specify when this development will take place..."
                        />
                    </Box>

                    {/* Question 6: Effectiveness Assessment */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            6. How will the effectiveness of the development be assessed?
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.effectiveness_assessment}
                            onChange={(e) => setFormData(prev => ({ ...prev, effectiveness_assessment: e.target.value }))}
                            placeholder="Describe how success will be measured..."
                        />
                    </Box>

                    {/* Question 7: Expected Changes */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            7. What changes are expected as a result of undertaking this development?
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.expected_changes}
                            onChange={(e) => setFormData(prev => ({ ...prev, expected_changes: e.target.value }))}
                            placeholder="Outline the expected outcomes and improvements..."
                        />
                    </Box>

                    {/* Question 8: Knowledge Sharing */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            8. How will the knowledge gained be shared within IOD PARC?
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.knowledge_sharing}
                            onChange={(e) => setFormData(prev => ({ ...prev, knowledge_sharing: e.target.value }))}
                            placeholder="Describe how you will share learnings with colleagues..."
                        />
                    </Box>

                    {/* Question 9: Proposed Option */}
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            9. What is the proposed option and your recommendation?
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.proposed_option}
                            onChange={(e) => setFormData(prev => ({ ...prev, proposed_option: e.target.value }))}
                            placeholder="Provide your final recommendation..."
                        />
                    </Box>

                    {/* Action Buttons */}
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={handleCancel}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SendIcon />}
                            onClick={() => setConfirmDialogOpen(true)}
                            disabled={isSubmitting}
                            sx={{ bgcolor: '#009688', '&:hover': { bgcolor: '#00796b' } }}
                        >
                            Submit PPD Form
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {/* Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>Confirm Submission</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to submit this PPD form? 
                    </Typography>
                    <Typography sx={{ mt: 2, color: 'text.secondary' }}>
                        Email notifications will be sent to:
                    </Typography>
                    <ul>
                        <li>mark@iodparc.com</li>
                        <li>{formData.manager_email || 'Your manager'}</li>
                        <li>You (confirmation)</li>
                    </ul>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{ bgcolor: '#009688', '&:hover': { bgcolor: '#00796b' } }}
                    >
                        {isSubmitting ? 'Submitting...' : 'Confirm Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default PPDFormPage;
